/**
 * Lumenate scroll reveal (LUM-35).
 *
 * Fades elements up as they enter the viewport, matching lumenate.co's AOS settings:
 * opacity 0 -> 1, a 100px rise, 1s ease-out, once, with a 300ms stagger between siblings
 * that share a row (live's 0/300/600 across its card rows).
 *
 * Rows are grouped at reveal time, not at arm time: elements that cross the trigger line
 * in the same observer batch are by definition entering together, and their geometry is
 * read from the intersection entry, so grouping reflects final layout rather than
 * whatever the page measured before images loaded and the webfont swapped in.
 *
 * Opt in with data-lumenate-reveal. The resting CSS state is the finished one - only this
 * script adds the armed class, so a script failure, a browser without IntersectionObserver
 * and reduced-motion readers all get visible content rather than an empty section.
 */
(() => {
  if (!('IntersectionObserver' in window)) return;

  const STAGGER_MS = 300;
  const ROW_TOLERANCE_PX = 8;
  const ARMED = 'lumenate-reveal--armed';
  const REVEALED = 'lumenate-reveal--in';

  /** Overflow values that clip a box instead of letting content spill out of it. */
  const CLIPPING_OVERFLOW = new Set(['auto', 'scroll', 'hidden', 'clip']);

  /** Scrolling ancestor -> the observer rooted at it, and the targets it still watches. */
  const scrollerObservers = new Map();
  /** Target -> the scrolling ancestor it is additionally observed against, if any. */
  const scrollerRoots = new WeakMap();

  function onEntering(entries) {
    // A target watched by both observers can appear in two batches in the same frame; the
    // first one to reveal it wins, and the second must not re-index its row.
    const entering = entries.filter(
      (entry) => entry.isIntersecting && !entry.target.classList.contains(REVEALED)
    );
    if (entering.length === 0) return;

    // Elements crossing the line in one batch are a row entering together. Group by
    // parent identity and top edge - measured here, so the geometry is final layout
    // rather than whatever it was before images and the webfont landed.
    const byParent = new Map();
    for (const entry of entering) {
      const element = entry.target;
      const top = entry.boundingClientRect.top;
      let rows = byParent.get(element.parentElement);
      if (!rows) {
        rows = [];
        byParent.set(element.parentElement, rows);
      }
      const row = rows.find((candidate) => Math.abs(candidate.top - top) <= ROW_TOLERANCE_PX);
      if (row) row.members.push(element);
      else rows.push({ top, members: [element] });
    }

    for (const rows of byParent.values()) {
      for (const row of rows) {
        // Stagger in document order, not visual order: `row-reverse`, an explicit
        // `order`, or RTL would read the other way round.
        row.members.sort((a, b) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        );
        row.members.forEach((element, index) => {
          if (index > 0) element.style.setProperty('--reveal-delay', `${index * STAGGER_MS}ms`);
          element.classList.add(REVEALED);
          release(element); // once, like AOS
        });
      }
    }
  }

  /** AOS's default offset: trigger 120px before the element reaches the viewport bottom. */
  const viewportObserver = new IntersectionObserver(onEntering, {
    rootMargin: '0px 0px -120px 0px',
  });

  /** Drops a target from every observer watching it, so a reveal happens exactly once. */
  function release(element) {
    viewportObserver.unobserve(element);

    const root = scrollerRoots.get(element);
    if (!root) return;
    scrollerRoots.delete(element);

    const scroller = scrollerObservers.get(root);
    if (!scroller) return;
    scroller.observer.unobserve(element);
    scroller.targets.delete(element);
    // Both the map entry and the observer hold the root element; drop them with the last
    // target so a torn-down section's scroller isn't retained for the rest of the session.
    if (scroller.targets.size === 0) {
      scroller.observer.disconnect();
      scrollerObservers.delete(root);
    }
  }

  /**
   * The nearest ancestor that clips its own overflowing content - a carousel or slideshow
   * track, say. An element parked outside such a box never intersects the viewport, so
   * viewport observation alone would leave it armed (invisible) until the visitor happens
   * to advance the scroller.
   *
   * The overflow gate has to require *actual* overflow: a merely `overflow: hidden` wrapper
   * that fits its content clips nothing, and rooting an observer at it would reveal its
   * children the moment the page loads, losing the effect.
   *
   * `.page-wrapper` and the document scrolling element are the page's own scrollers -
   * intersection against those is what the viewport observer already measures.
   */
  function scrollingAncestor(element) {
    for (let node = element.parentElement; node; node = node.parentElement) {
      if (node === document.scrollingElement || node.classList.contains('page-wrapper')) return null;

      const style = getComputedStyle(node);
      if (CLIPPING_OVERFLOW.has(style.overflowX) && node.scrollWidth > node.clientWidth) return node;
      if (CLIPPING_OVERFLOW.has(style.overflowY) && node.scrollHeight > node.clientHeight)
        return node;
    }
    return null;
  }

  /**
   * Also watch `element` against `root`, so whichever intersection fires first - the
   * viewport's or the scroller's - reveals it. One observer per root, shared by every
   * target inside it.
   *
   * No rootMargin here: the -120px trigger offset is a viewport reading distance, and on a
   * track shorter than 120px it would shrink the root to nothing and never fire at all.
   */
  function observeWithin(element, root) {
    let scroller = scrollerObservers.get(root);
    if (!scroller) {
      scroller = { observer: new IntersectionObserver(onEntering, { root }), targets: new Set() };
      scrollerObservers.set(root, scroller);
    }
    scroller.observer.observe(element);
    scroller.targets.add(element);
    scrollerRoots.set(element, root);
  }

  function arm(root) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Two passes. Arming writes a class, and resolving a scrolling ancestor reads computed
    // style and scroll geometry, so interleaving the two would force a style and layout
    // recalculation per target instead of one for the batch.
    const elements = [];
    const scrollers = [];
    for (const element of root.querySelectorAll('[data-lumenate-reveal]')) {
      if (element.classList.contains(ARMED)) continue;
      elements.push(element);
      scrollers.push(scrollingAncestor(element));
    }

    for (let index = 0; index < elements.length; index++) {
      const element = elements[index];
      element.classList.add(ARMED);
      viewportObserver.observe(element);
      if (scrollers[index]) observeWithin(element, scrollers[index]);
    }
  }

  function disarm(root) {
    for (const element of root.querySelectorAll('[data-lumenate-reveal]')) {
      release(element);
      element.classList.remove(ARMED);
      element.style.removeProperty('--reveal-delay');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => arm(document));
  } else {
    arm(document);
  }

  // The editor morphs sections in place; arm whatever markup it just rendered.
  document.addEventListener('shopify:section:load', (event) => arm(event.target));

  // The observers hold a strong reference to every target until it first intersects, so a
  // section the editor replaces before it was ever scrolled to would be retained for the
  // rest of the session. Drop those targets when the editor tears the section down.
  document.addEventListener('shopify:section:unload', (event) => disarm(event.target));
})();
